import React, { useState } from 'react';
import { CreditCard, Package, AlertCircle } from 'lucide-react';
import PayPalButton from '../components/payments/PayPalButton';
import useAuthStore from '../store/authStore';

const Credits: React.FC = () => {
  const [customAmount, setCustomAmount] = useState<string>('10');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { user } = useAuthStore();

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(value) && parseFloat(value || '0') <= 1000) {
      setCustomAmount(value);
    }
  };

  const handleInvalidAmount = () => {
    setError('Minimum payment amount is $2.00');
    setTimeout(() => setError(null), 5000);
  };

  const handlePaymentSuccess = (details: any) => {
    setSuccess('Payment successful! Credits have been added to your account.');
    setTimeout(() => setSuccess(null), 5000);
  };

  const handlePaymentError = (error: any) => {
    setError('There was an error processing your payment. Please try again or contact support.');
    setTimeout(() => setError(null), 5000);
  };

  const amount = parseFloat(customAmount) || 0;

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900">Add Credits</h1>
          <p className="mt-2 text-lg text-gray-600">
            Purchase credits to use our AI tools
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg">
            {success}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Current Balance</h2>
            <div className="flex items-center text-2xl font-bold text-gray-900">
              <CreditCard className="h-6 w-6 mr-2 text-cyan-600" />
              ${user?.credits.toFixed(2) || '0.00'}
            </div>
          </div>

          <div className="mb-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Custom Amount</h3>
                <Package className="h-5 w-5 text-gray-500" />
              </div>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                    Amount (USD) - Minimum $2.00
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="text"
                      id="amount"
                      value={customAmount}
                      onChange={handleAmountChange}
                      className="focus:ring-cyan-500 focus:border-cyan-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                      placeholder="Enter amount"
                    />
                  </div>
                </div>

                {amount >= 2 && (
                  <div className="text-sm text-gray-600">
                    {amount >= 500 ? (
                      <p className="text-green-600">10% volume discount will be applied!</p>
                    ) : amount >= 100 ? (
                      <p className="text-green-600">5% volume discount will be applied!</p>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>

          <PayPalButton
            amount={amount}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            onInvalidAmount={handleInvalidAmount}
          />

          <div className="mt-6 text-sm text-gray-500">
            <p>
              * Credits never expire and can be used across all our AI tools.
              <br />
              * Payments are processed securely through PayPal.
              <br />
              * Minimum payment amount: $2.00
              <br />
              * Get 5% bonus credits on purchases of $100 or more.
              <br />
              * Get 10% bonus credits on purchases of $500 or more.
              <br />
              * For custom enterprise packages, please contact support.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Credits;